// APP_TSX_VERSION: v2-homeroute-expiry-check
import React, { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import CookieConsentBanner from './components/CookieConsentBanner';
import { trackPageView } from './utils/analytics';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { FeatureFlagProvider, useFeatureFlags } from './contexts/FeatureFlagContext';
import { AdProvider } from './contexts/AdContext';
import HotelSearchResultsPage from './pages/HotelSearchResultsPage';
import GuestCheckoutPage from './pages/GuestCheckoutPage';
import FlightSearchResultsPage from './pages/FlightSearchResultsPage';
import CarSearchResultsPage from './pages/CarSearchResultsPage';
import ActivitySearchResultsPage from './pages/ActivitySearchResultsPage';

// Public pages (no auth required)
import AuthCallbackPage from './pages/AuthCallbackPage';
import DemoPage from './pages/DemoPage';
import TrustSafetyDemo from './pages/TrustSafetyDemo';
import ResourcesPage from './pages/ResourcesPage';
import AboutPage from './pages/AboutPage';
import WelcomePage from './pages/WelcomePage';
import FaqPage from './pages/FaqPage';
import ResourcePostPage from './pages/ResourcePostPage';
import LegalPage from './pages/LegalPage';
import EarlyAccessPage from './pages/EarlyAccessPage';
import LandingPage from './pages/LandingPage';
import SignupPage from './pages/SignupPage';
import ConfirmSignupPage from './pages/ConfirmSignupPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UnsubscribeConfirmPage from './pages/UnsubscribeConfirmPage';
import ContactPage from './pages/ContactPage';
import HomePage from './pages/HomePage';
import PlansPage from './pages/PlansPage';
import NotificationDetailPage from './components/NotificationDetailPage';
import NotificationsPage from './pages/NotificationsPage';

// Authenticated pages
import CreditsPage from './pages/CreditsPage';
import CreateTripPage from './pages/CreateTripPage';
import TripDetailPage from './pages/TripDetailPage';
import TripPhotosPage from './pages/TripPhotosPage';
import MyTripsPage from './pages/MyTripsPage';
import DestinationsPage from './pages/DestinationsPage';
import BookingPage from './pages/BookingPage';
import BookingCheckoutPage from './pages/BookingCheckoutPage';
import CommunityPage from './pages/CommunityPage';
import FriendsPage from './pages/FriendsPage';
import SettingsPage from './pages/SettingsPage';
import MyPhotosPage from './pages/MyPhotosPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminProfilePage from './pages/AdminProfilePage';
import AdminAdsPage from './pages/AdminAdsPage';
import { AppLayout, AdminLayout } from './components/layout';
import JoinTripPage from './pages/JoinTripPage';
import BookingGate from './components/common/BookingGate';
import { PwaInstallPrompt } from './components/PwaInstallPrompt';

// Home route - token existence AND expiry check.
// A merely-present token is not enough: an expired token here used to force a
// redirect to /home, where AppLayout's own auth check would then reject it and
// bounce the user to /login?redirect=%2Fhome — so a stale token meant the
// public landing page was never reachable. Decode the JWT payload locally
// (no network call) and only treat the token as valid if it hasn't expired.
const HomeRoute: React.FC = () => {
  const token = localStorage.getItem('idToken');
  if (!token) return <LandingPage />;

  try {
    const payloadSegment = token.split('.')[1];
    const payload = JSON.parse(atob(payloadSegment));
    const isExpired = typeof payload.exp === 'number' && payload.exp * 1000 < Date.now();
    if (isExpired) {
      localStorage.removeItem('idToken');
      return <LandingPage />;
    }
    return <Navigate to="/home" replace />;
  } catch {
    // Malformed/unparseable token - treat as invalid, clear it, show landing page.
    localStorage.removeItem('idToken');
    return <LandingPage />;
  }
};

// Community route - feature flag guarded.
// While flags load, render the page (safe default — community is mostly safe to expose briefly).
const CommunityRoute: React.FC = () => {
  const { isEnabled, isLoading } = useFeatureFlags();
  if (isLoading) {
    return <CommunityPage />;
  }
  const isCommunityPublic = isEnabled('community_public') === true;
  const isCommunityFriends = isEnabled('community_friends') === true;

  if (!isCommunityPublic && !isCommunityFriends) {
    return <Navigate to="/trips" replace />;
  }

  return <CommunityPage />;
};

// Destinations route - feature flag guarded.
// While flags load, render the page to avoid flash-redirect.
const DestinationsRoute: React.FC = () => {
  const { isEnabled, isLoading } = useFeatureFlags();
  if (isLoading) {
    return <DestinationsPage />;
  }
  const isDestinationsEnabled = isEnabled('destinations') === true;
  return isDestinationsEnabled ? <DestinationsPage /> : <Navigate to="/community" replace />;
};

// Credits route - feature flag guarded (MVP: disabled).
// While flags load, render nothing (avoid flashing the page for a feature that's disabled by default).
const CreditsRoute: React.FC = () => {
  const { isEnabled, isLoading } = useFeatureFlags();
  if (isLoading) {
    return null;
  }
  const isCreditsEnabled = isEnabled('credits_purchase_enabled') === true;
  return isCreditsEnabled
    ? <AppLayout showTabs={false}><CreditsPage /></AppLayout>
    : <Navigate to="/trips" replace />;
};

// Fires a self-hosted page-view beacon on every route change.
const PageViewTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  return null;
};

// Vercel Analytics: events are dropped (beforeSend -> null) until the user grants analytics consent.
const ConsentedVercelAnalytics: React.FC = () => {
  const { analyticsAllowed } = useCookieConsent();
  // beforeSend drops every event until consent — gates Vercel at the source
  // (conditional render alone doesn't stop Vercel's package-level init).
  return <Analytics beforeSend={(event) => (analyticsAllowed ? event : null)} />;
};

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CookieConsentProvider>
      <FeatureFlagProvider>
        <AdProvider>
          <Router>
            <PageViewTracker />
            <Routes>
              {/* Public routes - no AppLayout */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/confirm-signup" element={<ConfirmSignupPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/unsubscribe-confirm" element={<UnsubscribeConfirmPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FaqPage />} />

              {/* Authenticated routes - with AppLayout */}
              <Route path="/plans" element={
                  <AppLayout showTabs={true}>
                    <PlansPage />
                  </AppLayout>
                } />
              <Route path="/home" element={
                  <AppLayout showTabs={true}>
                    <HomePage />
                  </AppLayout>
                } />
              <Route path="/trips" element={
                <AppLayout>
                  <MyTripsPage />
                </AppLayout>
              } />

              {/* Create Trip - ALLOW GUEST ACCESS (Phase 1A) */}
              <Route path="/trips/new" element={
                <AppLayout requireAuth={false} showTabs={false}>
                  <CreateTripPage />
                </AppLayout>
              } />

              <Route path="/trips/:tripId/book" element={
                <AppLayout showTabs={false}>
                  <BookingGate>
                    <BookingCheckoutPage />
                  </BookingGate>
                </AppLayout>
              } />

              <Route path="/trips/:tripId" element={
                <AppLayout>
                  <TripDetailPage />
                </AppLayout>
              } />
              <Route path="/trips/:tripId/photos" element={
                <AppLayout>
                  <TripPhotosPage />
                </AppLayout>
              } />
              <Route path="/photos" element={
                <AppLayout>
                  <MyPhotosPage />
                </AppLayout>
              } />
              <Route path="/notifications/:id" element={
                <AppLayout showTabs={false}>
                  <NotificationDetailPage />
                </AppLayout>
              } />
              <Route path="/notifications" element={
                <AppLayout showTabs={false}>
                  <NotificationsPage />
                </AppLayout>
              } />
              <Route path="/trips/join/:token" element={
                <AppLayout requireAuth={false} showTabs={false}>
                  <JoinTripPage />
                </AppLayout>
              } />

              {/* Public-accessible routes - with AppLayout but no auth required */}
              <Route path="/community" element={
                <AppLayout requireAuth={false}>
                  <CommunityRoute />
                </AppLayout>
              } />
              <Route path="/destinations" element={
                <AppLayout requireAuth={false}>
                  <DestinationsRoute />
                </AppLayout>
              } />

              <Route path="/friends" element={
                <AppLayout requireAuth={false}>
                  <FriendsPage />
                </AppLayout>
              } />
              <Route path="/settings" element={
                <AppLayout showTabs={false}>
                  <SettingsPage />
                </AppLayout>
              } />
              <Route path="/credits" element={<CreditsRoute />} />

              {/* Booking surface - all gated by booking_enabled flag */}
              <Route path="/book" element={
                <AppLayout showTabs={false}>
                  <BookingGate>
                    <BookingPage />
                  </BookingGate>
                </AppLayout>
              } />

              <Route path="/search/hotels" element={
                <AppLayout requireAuth={false} showTabs={false}>
                  <BookingGate>
                    <HotelSearchResultsPage />
                  </BookingGate>
                </AppLayout>
              } />
              <Route path="/search/flights" element={
                <AppLayout requireAuth={false} showTabs={false}>
                  <BookingGate>
                    <FlightSearchResultsPage />
                  </BookingGate>
                </AppLayout>
              } />
              <Route path="/search/cars" element={
                <AppLayout requireAuth={false} showTabs={false}>
                  <BookingGate>
                    <CarSearchResultsPage />
                  </BookingGate>
                </AppLayout>
              } />
              <Route path="/search/activities" element={
                <AppLayout requireAuth={false} showTabs={false}>
                  <BookingGate>
                    <ActivitySearchResultsPage />
                  </BookingGate>
                </AppLayout>
              } />
              <Route path="/book/checkout" element={
                <AppLayout requireAuth={false} showTabs={false}>
                  <BookingGate>
                    <GuestCheckoutPage />
                  </BookingGate>
                </AppLayout>
              } />

              {/* Admin routes */}
              <Route path="/admin" element={
                <AdminLayout>
                  <AdminDashboardPage />
                </AdminLayout>
              } />
              <Route path="/admin/profile" element={
                <AdminLayout>
                  <AdminProfilePage />
                </AdminLayout>
              } />
              <Route path="/admin/ads" element={
                <AdminLayout>
                  <AdminAdsPage />
                </AdminLayout>
              } />

              {/* Redirects */}
              <Route path="/explore" element={<Navigate to="/community" replace />} />
              <Route path="/dashboard" element={<Navigate to="/community" replace />} />

              {/* Catch all */}
                            {/* Google OAuth callback — no auth required, handles token exchange */}
              <Route path="/early-access" element={<EarlyAccessPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/demo/trust-safety" element={<TrustSafetyDemo />} />
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/resources/:slug" element={<ResourcePostPage />} />
              <Route path="/terms" element={<LegalPage slug="terms" />} />
              <Route path="/privacy" element={<LegalPage slug="privacy" />} />
              <Route path="/aup" element={<LegalPage slug="aup" />} />
              <Route path="/cookie" element={<LegalPage slug="cookie" />} />
              <Route path="/data-deletion" element={<LegalPage slug="data-deletion" />} />
              <Route path="/dmca" element={<LegalPage slug="dmca" />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
<Route path="*" element={<HomeRoute />} />
            </Routes>
            <PwaInstallPrompt />
            <CookieConsentBanner />
            <ConsentedVercelAnalytics />
          </Router>
        </AdProvider>
      </FeatureFlagProvider>
      </CookieConsentProvider>
    </ThemeProvider>
  );
}
